import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  Share2,
  Facebook,
  Twitter,
  Wifi,
  Phone,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://flowing.lat";

// Company Info
const COMPANY_INFO = {
  phone: "44 35 30 00 30",
  whatsapp: "https://wa.me/message/TB7WYEMKU3L6G1",
};

// Static fallback posts
const STATIC_POSTS = [
  {
    id: "1",
    title: "¿Por qué la fibra óptica es mejor que el cable tradicional?",
    excerpt: "Descubre las ventajas de la tecnología de fibra óptica y cómo puede mejorar tu experiencia de navegación en internet.",
    content: `La fibra óptica representa un salto tecnológico significativo en la forma en que transmitimos datos. A diferencia del cable de cobre tradicional, la fibra utiliza pulsos de luz para transmitir información, lo que ofrece varias ventajas importantes:

## 1. Mayor velocidad
La fibra óptica puede alcanzar velocidades de hasta 1 Gbps o más, mientras que el cable tradicional generalmente se limita a 100-300 Mbps.

## 2. Menor latencia
La luz viaja más rápido que las señales eléctricas, lo que significa tiempos de respuesta más rápidos, ideal para gaming y videollamadas.

## 3. Mayor estabilidad
La fibra no se ve afectada por interferencias electromagnéticas, lo que garantiza una conexión más estable.

## 4. Simetría
Con fibra, puedes tener la misma velocidad de subida y bajada, perfecto para trabajo remoto y streaming.

En Flowing, utilizamos la mejor tecnología de fibra óptica para garantizarte la mejor experiencia de conexión en Tacámbaro.`,
    category: "Tecnología",
    date: "2026-01-15",
    image: "https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?w=1200&q=80",
    read_time: "5 min",
  },
  {
    id: "2",
    title: "Tips para mejorar la velocidad de tu WiFi en casa",
    excerpt: "Aprende cómo optimizar tu red WiFi para obtener el máximo rendimiento de tu conexión a internet.",
    content: `¿Tu WiFi no está rindiendo como esperabas? Aquí te compartimos algunos tips para optimizar tu conexión:

## 1. Ubicación del router
Coloca tu router en un lugar central de tu casa, elevado y alejado de paredes gruesas u objetos metálicos.

## 2. Actualiza el firmware
Mantén tu router actualizado para obtener las últimas mejoras de rendimiento y seguridad.

## 3. Cambia el canal WiFi
Si vives en una zona con muchas redes WiFi, cambiar a un canal menos congestionado puede mejorar significativamente tu velocidad.

## 4. Usa la banda de 5GHz
Si tu router tiene doble banda, usa la de 5GHz para dispositivos cercanos que necesiten mayor velocidad.

## 5. Reinicia periódicamente
Un reinicio semanal del router puede ayudar a mantener un rendimiento óptimo.

Si después de estos tips sigues teniendo problemas, contáctanos y uno de nuestros técnicos te ayudará a optimizar tu conexión.`,
    category: "Tips",
    date: "2026-01-10",
    image: "https://images.unsplash.com/photo-1606904825846-647eb07f5be2?w=1200&q=80",
    read_time: "4 min",
  },
  {
    id: "3",
    title: "Flowing Turbo: La nueva era del internet ultraveloz",
    excerpt: "Conoce nuestros nuevos planes Turbo con velocidades de hasta 500 Mbps para los usuarios más exigentes.",
    content: `Nos emociona presentar **Flowing Turbo**, nuestra nueva línea de planes premium diseñada para los usuarios más exigentes.

## ¿Qué es Flowing Turbo?
Es nuestra línea de internet de alta velocidad con planes desde 100 Mbps hasta 500 Mbps, pensada para hogares y negocios que necesitan el máximo rendimiento.

## Planes disponibles:
- **Plan 100** - 100 Mbps por solo $649/mes
- **Plan 200** - 200 Mbps por solo $990/mes
- **Plan 500** - 500 Mbps por solo $1,249/mes

## Beneficios exclusivos:
- Latencia ultra baja, ideal para gaming
- Streaming en 4K sin interrupciones
- Soporte técnico prioritario
- Ideal para trabajo remoto con videollamadas HD

## ¿Para quién es Turbo?
- Gamers que necesitan baja latencia
- Streamers y creadores de contenido
- Familias con múltiples dispositivos conectados
- Profesionales que trabajan desde casa
- Empresas que necesitan alta disponibilidad

¡Contáctanos hoy y únete a la era del internet ultraveloz!`,
    category: "Noticias",
    date: "2026-01-05",
    image: "https://images.unsplash.com/photo-1607723619497-98a79f01ba90?w=1200&q=80",
    read_time: "3 min",
  },
  {
    id: "4",
    title: "Streaming en 4K: ¿Cuánta velocidad necesitas?",
    excerpt: "Te explicamos los requisitos de velocidad para disfrutar de contenido en alta definición sin interrupciones.",
    content: `Con el auge de las plataformas de streaming como Netflix, Disney+, y Amazon Prime, es importante conocer cuánta velocidad necesitas para disfrutar de contenido sin buffering.

## Requisitos por calidad:
- **SD (480p):** 3 Mbps
- **HD (720p):** 5 Mbps
- **Full HD (1080p):** 10 Mbps
- **4K Ultra HD:** 25 Mbps
- **4K HDR:** 40 Mbps

## Consideraciones importantes:

### 1. Múltiples dispositivos
Si en tu hogar hay varios dispositivos conectados simultáneamente, multiplica los requisitos. Por ejemplo, 3 TVs en 4K necesitarían al menos 75 Mbps.

### 2. Otros usos
Recuerda que el streaming no es lo único que usa tu internet. Videollamadas, descargas y navegación también consumen ancho de banda.

### 3. Margen de seguridad
Recomendamos contratar al menos un 50% más de velocidad de lo que calculas necesitar para garantizar una experiencia fluida.

## Nuestra recomendación:
- Hogar pequeño (1-2 personas): Plan de 30-50 Mbps
- Familia (3-5 personas): Plan de 50-100 Mbps
- Usuarios intensivos o gamers: Flowing Turbo 200-500 Mbps

¿No estás seguro de qué plan necesitas? Contáctanos y te asesoramos sin compromiso.`,
    category: "Tips",
    date: "2025-12-28",
    image: "https://images.unsplash.com/photo-1691435828932-911a7801adfb?w=1200&q=80",
    read_time: "4 min",
  },
];

// Format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-MX', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

// Escape HTML special characters to prevent XSS
const escapeHtml = (text) => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

// Parse markdown-like content to HTML with XSS protection
const parseContent = (content) => {
  if (!content) return "";
  
  const rawHtml = content
    .split('\n')
    .map(line => {
      // First escape any HTML in the line to prevent XSS
      const escapedLine = escapeHtml(line);
      
      // Headers (check original line for markdown syntax)
      if (line.startsWith('## ')) {
        return `<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">${escapeHtml(line.slice(3))}</h2>`;
      }
      if (line.startsWith('### ')) {
        return `<h3 class="text-xl font-semibold text-gray-900 mt-6 mb-3">${escapeHtml(line.slice(4))}</h3>`;
      }
      
      // Bold (apply to escaped content)
      let processedLine = escapedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
      
      // List items
      if (line.startsWith('- ')) {
        return `<li class="ml-4 text-gray-700">${escapeHtml(line.slice(2)).replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')}</li>`;
      }
      
      // Empty line = paragraph break
      if (line.trim() === '') {
        return '<br/>';
      }
      
      // Regular paragraph
      return `<p class="text-gray-700 mb-4">${processedLine}</p>`;
    })
    .join('');
  
  // Final sanitization with DOMPurify as defense in depth
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ['h2', 'h3', 'p', 'strong', 'li', 'br', 'ul', 'ol'],
    ALLOWED_ATTR: ['class']
  });
};

// Blog List Page
export const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  const fetchPosts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?page=${page}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        if (data.posts && data.posts.length > 0) {
          setPosts(data.posts);
          setPagination(data.pagination);
        } else {
          setPosts(STATIC_POSTS);
          setPagination({ page: 1, limit: 10, total: STATIC_POSTS.length, total_pages: 1, has_next: false, has_prev: false });
        }
      } else {
        setPosts(STATIC_POSTS);
      }
    } catch (error) {
      setPosts(STATIC_POSTS);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts(1);
  }, []);

  const handlePageChange = (newPage) => {
    fetchPosts(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Volver al inicio
            </Link>
            <a
              href={COMPANY_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full font-medium hover:bg-orange-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Contactar
            </a>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Blog Flowing</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Tips, noticias y novedades sobre tecnología e internet
            </p>
          </motion.div>
        </div>
      </header>

      {/* Posts Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 mt-4">Cargando artículos...</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.id}`}
                    className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.created_at || post.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.read_time || post.readTime}
                        </span>
                      </div>
                      <h2 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                      <div className="mt-4 text-orange-500 font-medium text-sm group-hover:underline">
                        Leer más →
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="pagination-prev-btn"
                >
                  ← Anterior
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        pageNum === pagination.page
                          ? "bg-orange-500 text-white"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      data-testid={`pagination-page-${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="pagination-next-btn"
                >
                  Siguiente →
                </button>
              </div>
            )}

            {/* Page info */}
            {pagination.total > 0 && (
              <p className="text-center text-gray-500 mt-4 text-sm">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} artículos
              </p>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-flowing.png" 
                alt="Flowing" 
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Single Blog Post Page
export const BlogPostPage = () => {
  const { id: postId } = useParams();  // Route param is :id, renamed to postId for clarity
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Try to fetch from API
        const response = await fetch(`${API_URL}/api/blog/posts/${postId}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
        } else {
          // Fallback to static posts
          const staticPost = STATIC_POSTS.find(p => p.id === postId);
          if (staticPost) {
            setPost(staticPost);
          }
        }
        
        // Fetch related posts (now with pagination)
        const allResponse = await fetch(`${API_URL}/api/blog/posts?limit=10`);
        if (allResponse.ok) {
          const allData = await allResponse.json();
          const allPosts = allData.posts || allData;
          setRelatedPosts(allPosts.filter(p => p.id !== postId).slice(0, 3));
        } else {
          setRelatedPosts(STATIC_POSTS.filter(p => p.id !== postId).slice(0, 3));
        }
      } catch (error) {
        // Fallback to static
        const staticPost = STATIC_POSTS.find(p => p.id === postId);
        if (staticPost) {
          setPost(staticPost);
        }
        setRelatedPosts(STATIC_POSTS.filter(p => p.id !== postId).slice(0, 3));
      }
      setLoading(false);
    };
    
    fetchPost();
    window.scrollTo(0, 0);
  }, [postId]);

  // Update meta tags dynamically for social sharing
  useEffect(() => {
    if (!post) return;
    
    const postUrl = `${SITE_URL}/blog/${post.id}`;
    const pageTitle = `${post.title} | Blog Flowing`;
    
    // Update document title
    document.title = pageTitle;
    
    // Helper function to update or create meta tags
    const setMetaTag = (property, content, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let element = document.querySelector(`meta[${attr}="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    
    // Open Graph tags
    setMetaTag('og:type', 'article');
    setMetaTag('og:url', postUrl);
    setMetaTag('og:title', post.title);
    setMetaTag('og:description', post.excerpt);
    setMetaTag('og:image', post.image);
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');
    setMetaTag('og:site_name', 'Flowing - Internet Fibra Óptica');
    setMetaTag('og:locale', 'es_MX');
    setMetaTag('article:section', post.category);
    
    // Twitter tags
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', postUrl, true);
    setMetaTag('twitter:title', post.title, true);
    setMetaTag('twitter:description', post.excerpt, true);
    setMetaTag('twitter:image', post.image, true);
    
    // Description
    setMetaTag('description', post.excerpt, true);
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', postUrl);
    
    // Cleanup function - restore original meta tags when leaving page
    return () => {
      document.title = 'Flowing | Internet por Fibra Óptica en Tacámbaro, Michoacán | Velocidades hasta 500 Mbps';
      setMetaTag('og:type', 'website');
      setMetaTag('og:url', 'https://flowing.lat/');
      setMetaTag('og:title', 'Flowing | Internet por Fibra Óptica en Tacámbaro - Hasta 500 Mbps');
      setMetaTag('og:description', 'La mejor conexión de internet por fibra óptica en Tacámbaro, Michoacán. Planes para hogar y empresa con velocidades hasta 500 Mbps. 99.9% uptime. Soporte 24/7.');
      setMetaTag('og:image', 'https://flowing.lat/logo-flowing.png');
      setMetaTag('twitter:title', 'Flowing | Internet por Fibra Óptica en Tacámbaro - Hasta 500 Mbps', true);
      setMetaTag('twitter:description', 'La mejor conexión de internet por fibra óptica en Tacámbaro, Michoacán. Planes para hogar y empresa. 99.9% uptime. Soporte 24/7.', true);
      setMetaTag('twitter:image', 'https://flowing.lat/logo-flowing.png', true);
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-500 mt-4">Cargando artículo...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="text-orange-500 hover:underline">
            ← Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareText = encodeURIComponent(post.title);
  const postUrl = `${SITE_URL}/blog/${post.id}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
        
        {/* Back button */}
        <div className="absolute top-6 left-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 bg-white/10 backdrop-blur text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {formatDate(post.created_at || post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.read_time || post.readTime} de lectura
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Share buttons */}
        <div className="flex items-center gap-4 mb-8 pb-8 border-b border-gray-200">
          <span className="text-gray-500 font-medium">Compartir:</span>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
          >
            <Share2 className="w-5 h-5" />
          </a>
        </div>

        {/* Article content */}
        <article 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: parseContent(post.content) }}
        />

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">¿Listo para mejorar tu conexión?</h3>
          <p className="text-white/90 mb-6">
            Contáctanos y descubre el plan perfecto para ti
          </p>
          <a
            href={COMPANY_INFO.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            <Phone className="w-5 h-5" />
            Contactar por WhatsApp
          </a>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Artículos relacionados</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relPost) => (
                <Link
                  key={relPost.id}
                  to={`/blog/${relPost.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={relPost.image}
                      alt={relPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-orange-500 font-medium">{relPost.category}</span>
                    <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2 group-hover:text-orange-500 transition-colors">
                      {relPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo-flowing.png" 
                alt="Flowing" 
                className="h-8 w-auto"
              />
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
                Blog
              </Link>
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Inicio
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogListPage;
